// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — a TODO's priority. Pure: no DOM, no network.
//
// ⚠ PRIORITY IS NOT A PROPERTY. It is the item's own `marker`, and its values are
// a list the PQFORCE client HARDCODES — the three `prioMarker` entries below,
// copied from it verbatim. The server stores any string in `marker`; one not in
// this list is one the PQFORCE client cannot draw either, so it is NO priority.
//
// The server maps a stored marker to the `id` below (`PQF_MOBILE_PRIORITY_MARKERS`
// in mylibs/pqforce-mobile-todos.php) and back again on create, so the app never
// handles the marker key itself. It is kept here all the same, so the two lists
// can be compared: `MobilePriorityMirrorTest` fails if their pairs differ.
// ─────────────────────────────────────────────────────────────────────────────

import { type Priority } from "home/app/mobile/types";
import { currentLanguage } from "home/app/mobile/i18n";

export interface TodoPriority {
    /** What the app and the portal's API call it. */
    id: Priority;
    /** What `pi.ProjectItem.marker` stores. Case-sensitive, like every PQFORCE id. */
    marker: string;
    /** The PQFORCE client's own names, so the app calls a priority what PQFORCE calls it. */
    name: Readonly<Record<string, string>>;
    /** `#rrggbb`, the PQFORCE client's own. A constant, never tenant data. */
    color: string;
}

/** In the PQFORCE client's order: low to high. The composer offers them in this order. */
export const TODO_PRIORITIES: readonly TodoPriority[] = [
    {
        id: "prio_low",
        marker: "CDE68BA071A2445F8B58D770B212E008",
        name: { en: "Low", de: "Tief", fr: "Faible", it: "Bassa", "zh-hans": "低", "zh-hant": "低" },
        color: "#67d1ff",
    },
    {
        id: "prio_medium",
        marker: "DF8586B06ADA4DA587BE1936BEE2022D",
        name: { en: "Medium", de: "Mittel", fr: "Moyenne", it: "Media", "zh-hans": "中", "zh-hant": "中" },
        color: "#fcc01a",
    },
    {
        id: "prio_high",
        marker: "1F08A59E18FB489D9CC4649CD5F4BF83",
        name: { en: "High", de: "Hoch", fr: "Élevée", it: "Alta", "zh-hans": "高", "zh-hant": "高" },
        color: "#cb4647",
    },
];

/** The catalog entry for a wire value, or null — for none, and for anything the catalog does not know. */
export function priorityOf(value: unknown): TodoPriority | null {
    return TODO_PRIORITIES.find((priority) => priority.id === value) ?? null;
}

/** The priority's name in `language` (the app's own by default), else in English. `""` for no priority. */
export function priorityName(value: unknown, language: string = currentLanguage()): string {
    const priority = priorityOf(value);
    if (!priority) return "";

    return priority.name[language] ?? priority.name.en ?? priority.id;
}

/**
 * The text colour for a chip FILLED with a priority's colour: near-black on a light fill, white on a
 * dark one, decided by the fill's relative luminance (WCAG). Computed rather than stored beside each
 * colour, so the catalog stays a verbatim copy of the PQFORCE client's and a changed colour cannot
 * leave an unreadable pairing behind. Measured: Low and Medium are light, High is dark.
 */
export function priorityInk(color: string): string {
    const hex = /^#([0-9a-f]{6})$/i.exec(color.trim());
    if (!hex) return "#07074e";

    const channel = (offset: number) => {
        const c = parseInt(hex[1]!.slice(offset, offset + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);

    return luminance > 0.35 ? "#07074e" : "#ffffff";
}
